import { useEffect, useState, type ReactNode } from "react";
import {
  Activity,
  BarChart3,
  Camera,
  MessageSquare,
  PanelLeftClose,
  PanelLeftOpen,
  ScanBarcode,
  Search,
  Settings as SettingsIcon,
  Users as UsersIcon,
  Zap,
} from "lucide-react";
import { cx } from "../utils/cx";

type Tab =
  | "stats"
  | "reports"
  | "foods"
  | "scans"
  | "barcodes"
  | "users"
  | "compare"
  | "endpoints"
  | "settings";

const NAV: { id: Tab; label: string; Icon: typeof BarChart3 }[] = [
  { id: "stats", label: "Insights", Icon: BarChart3 },
  { id: "foods", label: "Foods", Icon: Search },
  { id: "scans", label: "Scans", Icon: Camera },
  { id: "barcodes", label: "Barcodes", Icon: ScanBarcode },
  { id: "users", label: "Users", Icon: UsersIcon },
  { id: "compare", label: "Compare", Icon: Zap },
  { id: "endpoints", label: "Endpoints", Icon: Activity },
  { id: "reports", label: "Reports", Icon: MessageSquare },
  { id: "settings", label: "Settings", Icon: SettingsIcon },
];

const COLLAPSED_KEY = "fooddb.admin.sidebarCollapsed";

export function Shell({
  tab,
  onTabChange,
  env,
  children,
}: {
  tab: Tab;
  onTabChange: (t: Tab) => void;
  env: string | null;
  children: ReactNode;
}) {
  const settingsIndex = NAV.findIndex((n) => n.id === "settings");

  // Sidebar collapse state. Persisted across reloads so the layout doesn't
  // re-toggle on every page open. Mobile (<lg) always shows the horizontal
  // top bar variant — collapse only applies to lg+.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      /* ignore — localStorage disabled */
    }
  }, [collapsed]);

  return (
    <div
      className={cx(
        "grid min-h-screen grid-cols-1 bg-stone-50 text-gray-950 dark:bg-transparent dark:text-gray-50",
        collapsed ? "lg:grid-cols-[64px_1fr]" : "lg:grid-cols-[232px_1fr]",
      )}
    >
      <aside className="sticky top-0 z-20 flex max-h-screen flex-col gap-1 overflow-y-auto border-b border-stone-200/80 bg-stone-50/95 p-3 backdrop-blur dark:border-white/8 dark:border-b-white/8 dark:bg-[#0b0d10]/95 lg:h-screen lg:border-r lg:border-b-0">
        <SidebarHeader
          env={env}
          collapsed={collapsed}
          onToggle={() => setCollapsed((c) => !c)}
        />

        <nav className="scrollbar-none flex flex-row gap-1 overflow-x-auto pb-1 lg:mt-2 lg:flex-col lg:overflow-visible lg:pb-0">
          {NAV.map((n, i) => (
            <div key={n.id} className="shrink-0 lg:shrink">
              {i === settingsIndex && (
                <div className="mx-1.5 my-2 hidden h-px bg-stone-200 dark:bg-white/10 lg:block" />
              )}
              <NavButton
                active={tab === n.id}
                Icon={n.Icon}
                label={n.label}
                collapsed={collapsed}
                onClick={() => onTabChange(n.id)}
              />
            </div>
          ))}
        </nav>
      </aside>

      <main className="mx-auto w-full max-w-[1360px] px-4 py-6 sm:px-6 lg:p-8 xl:p-10">
        {children}
      </main>
    </div>
  );
}

// ── Sidebar header (FoodDB / Admin · env) ─────────────────────────────────

function SidebarHeader({
  env,
  collapsed,
  onToggle,
}: {
  env: string | null;
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="mb-4 flex items-start justify-between gap-2 px-2 py-1">
      <div className="min-w-0">
        {/* Hide the wordmark in collapsed mode — the toggle button is the
            only header content the rail needs. The brand returns the
            moment the user expands. */}
        {!collapsed && (
          <>
            <span className="block truncate text-[15px] font-semibold tracking-tight text-gray-950 dark:text-gray-50">
              FoodDB
            </span>
            <div className="mt-0.5 flex items-center gap-2">
              <span className="truncate text-xs text-gray-500 dark:text-gray-500">
                Admin
              </span>
              {env && <EnvBadge env={env} />}
            </div>
          </>
        )}
      </div>

      {/* Collapse / expand affordance. Hidden on mobile where the sidebar
          becomes a horizontal top bar (collapse doesn't make sense). */}
      <button
        type="button"
        onClick={onToggle}
        aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        className={cx(
          "hidden shrink-0 items-center justify-center rounded-md p-1.5 text-gray-400 transition-colors lg:inline-flex",
          "hover:bg-stone-200/60 hover:text-gray-700",
          "dark:text-gray-500 dark:hover:bg-white/[0.06] dark:hover:text-gray-200",
        )}
      >
        {collapsed ? (
          <PanelLeftOpen size={16} strokeWidth={1.75} />
        ) : (
          <PanelLeftClose size={16} strokeWidth={1.75} />
        )}
      </button>
    </div>
  );
}

// ── Nav button ────────────────────────────────────────────────────────────

function NavButton({
  active,
  Icon,
  label,
  collapsed,
  onClick,
}: {
  active: boolean;
  Icon: typeof BarChart3;
  label: string;
  collapsed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={collapsed ? label : undefined}
      className={cx(
        "relative flex h-9 w-full items-center gap-2.5 rounded-md text-sm font-medium transition-colors",
        collapsed ? "lg:justify-center lg:px-0" : "px-2.5 text-left",
        active
          ? "bg-stone-200 text-gray-950 dark:bg-white/8 dark:text-gray-50"
          : "text-gray-600 hover:bg-stone-200/70 hover:text-gray-950 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-100",
      )}
    >
      <Icon
        size={15}
        strokeWidth={2}
        className={
          active
            ? "text-gray-950 dark:text-gray-50"
            : "text-gray-400 dark:text-gray-500"
        }
      />
      {!collapsed && label}
      {collapsed && <span className="sr-only">{label}</span>}
    </button>
  );
}

// ── Environment badge ─────────────────────────────────────────────────────
// Slim inline marker rather than a chunky pill: a 6px tinted dot followed
// by the env name. Sits beside the "Admin" subtitle so the header reads as
// one line ("Admin · local") instead of two stacked elements competing for
// attention. Color is consistent with production = red, staging = amber,
// anything else (local / dev) = teal.

function EnvBadge({ env }: { env: string }) {
  const dot =
    env === "production"
      ? "bg-red-500"
      : env === "staging"
        ? "bg-amber-500"
        : "bg-teal-500";
  return (
    <span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
      <span className={cx("size-1.5 rounded-full", dot)} aria-hidden />
      {env}
    </span>
  );
}
